import React, { useState, useEffect, useRef } from 'react';

const API = 'https://talku.online';
const SF = "'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// Parámetros reales aceptados por backend/app/api/routes/video.py
const MODELS = [
  { id: 'sora-2-pro', label: 'Sora 2 Pro' },
  { id: 'sora-2', label: 'Sora 2' },
];
const SIZES = {
  'sora-2-pro': ['1280x720', '720x1280', '1920x1080', '1080x1920', '1792x1024', '1024x1792', '480x480'],
  'sora-2': ['1280x720', '720x1280'],
};
const DURATIONS = [8, 16, 20]; // segundos permitidos por el backend

const STATUS_META = {
  queued: { label: 'En cola', tone: 'amber' },
  pending: { label: 'En cola', tone: 'amber' },
  in_progress: { label: 'Procesando', tone: 'blue' },
  completed: { label: 'Listo', tone: 'green' },
  failed: { label: 'Falló', tone: 'red' },
};

export default function VideoAI({ user }) {
  const authH = () => ({
    Authorization: `Bearer ${user?.token || localStorage.getItem('talku_token') || ''}`,
    'Content-Type': 'application/json',
  });

  const [prompt, setPrompt] = useState('');
  const [model, setModel] = useState('sora-2-pro');
  const [size, setSize] = useState('1080x1920');
  const [seconds, setSeconds] = useState(16);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [downloading, setDownloading] = useState(null);

  const jobsRef = useRef([]);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);

  // Al cambiar de modelo, si el tamaño actual no es válido, tomar el primero válido
  const pickModel = (m) => {
    setModel(m);
    if (!SIZES[m].includes(size)) setSize(SIZES[m][0]);
  };

  // Polling cada 12 s de los jobs activos
  useEffect(() => {
    const iv = setInterval(async () => {
      const active = jobsRef.current.filter(j => j.id && j.status !== 'completed' && j.status !== 'failed');
      for (const j of active) {
        try {
          const r = await fetch(`${API}/video/status/${j.id}`, { headers: authH() });
          if (!r.ok) continue;
          const d = await r.json();
          setJobs(prev => prev.map(x => (x.id === j.id ? { ...x, ...d } : x)));
        } catch { /* ignore */ }
      }
    }, 12000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const generate = async () => {
    if (!prompt.trim() || generating) return;
    setError('');
    setGenerating(true);
    try {
      const res = await fetch(`${API}/video/generate`, {
        method: 'POST',
        headers: authH(),
        body: JSON.stringify({ prompt: prompt.trim(), model, size, seconds }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        setError(e.detail || `Error ${res.status}`);
        setGenerating(false);
        return;
      }
      const d = await res.json();
      const job = { ...d, prompt: prompt.trim(), model, size, seconds, created: new Date().toISOString() };
      setJobs(prev => [job, ...prev]);
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    }
    setGenerating(false);
  };

  const download = async (j) => {
    if (downloading) return;
    setDownloading(j.id);
    try {
      const r = await fetch(`${API}/video/download/${j.id}`, { headers: authH() });
      if (!r.ok) { setError(`No se pudo descargar el video (${r.status}).`); setDownloading(null); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `video-${j.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      setError('Error al descargar el video.');
    }
    setDownloading(null);
  };

  const sizes = SIZES[model];

  return (
    <div className="vai-root">
      <style>{`
        .vai-root { flex:1; overflow-y:auto; background:#F5F5F3; font-family:${SF}; padding:40px; }
        .vai-inner { max-width:900px; margin:0 auto; }
        .vai-root::-webkit-scrollbar { width:6px; } .vai-root::-webkit-scrollbar-thumb { background:#d8d8d4; border-radius:3px; }
        .vai-h1 { font-size:26px; font-weight:640; color:#0F0F10; letter-spacing:-.4px; margin:0; }
        .vai-sub { font-size:13px; color:#6B7280; margin:6px 0 24px; }
        .vai-card { background:#fff; border-radius:20px; padding:26px; box-shadow:0 1px 2px rgba(0,0,0,.04); margin-bottom:24px; }
        .vai-label { font-size:11px; font-weight:600; letter-spacing:.05em; text-transform:uppercase; color:#9CA3AF; margin-bottom:10px; display:block; }
        .vai-textarea { width:100%; min-height:96px; resize:vertical; border:1px solid #ECECEC; border-radius:14px; padding:14px 16px; font-family:${SF}; font-size:14px; color:#0F0F10; outline:none; transition:border-color .15s; box-sizing:border-box; }
        .vai-textarea:focus { border-color:#6D5AE0; }
        .vai-textarea::placeholder { color:#B4B4B4; }
        .vai-group { margin-top:20px; }
        .vai-opts { display:flex; gap:8px; flex-wrap:wrap; }
        .vai-opt { padding:9px 15px; border-radius:10px; border:1px solid #ECECEC; background:#fff; font-size:13px; font-weight:500; color:#374151; cursor:pointer; transition:all .15s; font-family:${SF}; }
        .vai-opt:hover { border-color:#D8D4F2; }
        .vai-opt.active { background:#6D5AE0; border-color:#6D5AE0; color:#fff; }
        .vai-actions { display:flex; align-items:center; gap:16px; margin-top:26px; }
        .vai-btn { height:46px; padding:0 26px; background:#6D5AE0; color:#fff; border:none; border-radius:12px; font-family:${SF}; font-size:14px; font-weight:600; cursor:pointer; transition:opacity .15s; }
        .vai-btn:disabled { opacity:.5; cursor:not-allowed; }
        .vai-btn:hover:not(:disabled) { opacity:.9; }
        .vai-err { background:#FBE4E4; color:#D94A4A; font-size:13px; border-radius:10px; padding:10px 14px; margin-top:16px; }
        .vai-hist-title { font-size:15px; font-weight:600; color:#0F0F10; margin:0 0 4px; }
        .vai-empty { font-size:13px; color:#9CA3AF; text-align:center; padding:26px 0; }
        .vai-job { display:flex; align-items:center; gap:14px; padding:15px 0; }
        .vai-job + .vai-job { border-top:1px solid #F4F4F2; }
        .vai-job-main { flex:1; min-width:0; }
        .vai-job-prompt { font-size:13.5px; color:#0F0F10; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .vai-job-meta { font-size:11.5px; color:#9CA3AF; margin-top:3px; }
        .vai-badge { font-size:11px; font-weight:600; padding:4px 10px; border-radius:100px; white-space:nowrap; }
        .vai-badge.amber { background:#F7EFDC; color:#B45309; }
        .vai-badge.blue  { background:#E7EDF8; color:#4F79C7; }
        .vai-badge.green { background:#E4F3EA; color:#2FA468; }
        .vai-badge.red   { background:#FBE4E4; color:#D94A4A; }
        .vai-dl { height:34px; padding:0 14px; background:#0F0F10; color:#fff; border:none; border-radius:9px; font-family:${SF}; font-size:12.5px; font-weight:600; cursor:pointer; white-space:nowrap; }
        .vai-dl:disabled { opacity:.5; cursor:not-allowed; }
        .vai-job-err { font-size:11.5px; color:#D94A4A; max-width:220px; text-align:right; }
      `}</style>

      <div className="vai-inner">
        <h1 className="vai-h1">Video IA</h1>
        <p className="vai-sub">Genera videos con Sora a partir de un prompt.</p>

        <div className="vai-card">
          <label className="vai-label">Prompt</label>
          <textarea
            className="vai-textarea"
            placeholder="Describe el video que quieres generar…"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
          />

          <div className="vai-group">
            <span className="vai-label">Modelo</span>
            <div className="vai-opts">
              {MODELS.map(m => (
                <button key={m.id} className={`vai-opt ${model === m.id ? 'active' : ''}`} onClick={() => pickModel(m.id)}>{m.label}</button>
              ))}
            </div>
          </div>

          <div className="vai-group">
            <span className="vai-label">Resolución</span>
            <div className="vai-opts">
              {sizes.map(s => (
                <button key={s} className={`vai-opt ${size === s ? 'active' : ''}`} onClick={() => setSize(s)}>{s}</button>
              ))}
            </div>
          </div>

          <div className="vai-group">
            <span className="vai-label">Duración</span>
            <div className="vai-opts">
              {DURATIONS.map(d => (
                <button key={d} className={`vai-opt ${seconds === d ? 'active' : ''}`} onClick={() => setSeconds(d)}>{d}s</button>
              ))}
            </div>
          </div>

          <div className="vai-actions">
            <button className="vai-btn" onClick={generate} disabled={generating || !prompt.trim()}>
              {generating ? 'Generando…' : 'Generar video'}
            </button>
          </div>

          {error && <div className="vai-err">{error}</div>}
        </div>

        <div className="vai-card">
          <h2 className="vai-hist-title">Historial</h2>
          {jobs.length === 0 ? (
            <div className="vai-empty">Aún no has generado videos en esta sesión.</div>
          ) : (
            jobs.map(j => {
              const meta = STATUS_META[j.status] || { label: j.status || '—', tone: 'amber' };
              return (
                <div className="vai-job" key={j.id}>
                  <div className="vai-job-main">
                    <div className="vai-job-prompt" title={j.prompt}>{j.prompt}</div>
                    <div className="vai-job-meta">
                      {j.model} · {j.size} · {j.seconds}s
                      {j.status === 'in_progress' && typeof j.progress === 'number' ? ` · ${j.progress}%` : ''}
                    </div>
                  </div>
                  <span className={`vai-badge ${meta.tone}`}>{meta.label}</span>
                  {j.status === 'completed' && (
                    <button className="vai-dl" onClick={() => download(j)} disabled={downloading === j.id}>
                      {downloading === j.id ? 'Descargando…' : '↓ Descargar'}
                    </button>
                  )}
                  {j.status === 'failed' && <span className="vai-job-err">{j.error || 'Error'}</span>}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
